import { format } from 'date-fns';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type OutreachTrendDay } from '@libraries/typescript/api_client';

type OutcomeTrendChartProps = {
    readonly days: Array<OutreachTrendDay>;
    // Fires with the clicked bar's ET date key (YYYY-MM-DD) for a runs drill.
    readonly onDayClick?: (day: string) => void;
};

type TrendPoint = {
    readonly label: string;
    // Raw ET date key (YYYY-MM-DD) the bar represents, for drill filters.
    readonly day: string;
    readonly filled: number;
    readonly contactedNoYes: number;
    readonly noCaregiversSuggested: number;
    readonly shiftCancelled: number;
    readonly otherNonFill: number;
    readonly inFlight: number;
};

const SERIES: Array<{
    readonly key: keyof Omit<TrendPoint, 'day'>;
    readonly label: string;
    readonly color: string;
}> = [
    { key: 'filled', label: 'Filled callouts', color: 'var(--chart-2)' },
    {
        key: 'contactedNoYes',
        label: 'Contacted, no yes',
        color: 'var(--chart-1)',
    },
    {
        key: 'noCaregiversSuggested',
        label: 'No caregivers suggested',
        color: 'var(--destructive)',
    },
    {
        key: 'shiftCancelled',
        label: 'Shift cancelled',
        color: 'var(--chart-4)',
    },
    { key: 'otherNonFill', label: 'Other non-fill', color: 'var(--chart-5)' },
    { key: 'inFlight', label: 'In flight', color: 'var(--muted-foreground)' },
];

function toTrendPoint(day: OutreachTrendDay): TrendPoint {
    const buckets = day.non_fill_bucket_counts;
    const filled = day.filled_count;
    const contactedNoYes = buckets['contacted_no_yes'] ?? 0;
    const noCaregiversSuggested = buckets['no_caregivers_suggested'] ?? 0;
    const shiftCancelled = buckets['shift_cancelled'] ?? 0;
    const totalNonFill = Object.values(buckets).reduce(
        (sum, count) => sum + count,
        0,
    );
    return {
        label: format(new Date(`${day.day}T00:00:00`), 'M/d'),
        day: day.day,
        filled,
        contactedNoYes,
        noCaregiversSuggested,
        shiftCancelled,
        otherNonFill:
            totalNonFill -
            contactedNoYes -
            noCaregiversSuggested -
            shiftCancelled,
        inFlight: Math.max(0, day.num_runs - filled - totalNonFill),
    };
}

type TrendTooltipEntry = {
    readonly name?: string;
    readonly value?: number;
    readonly color?: string;
};

function TrendTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<TrendTooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.name}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">{entry.value}</span>
                </div>
            ))}
        </div>
    );
}

export function OutcomeTrendChart({
    days,
    onDayClick,
}: OutcomeTrendChartProps) {
    const data = days.map(toTrendPoint);
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-72 items-center justify-center rounded-md border text-sm">
                No data to plot.
            </div>
        );
    }

    return (
        <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{ left: 8, right: 8 }}
                    onClick={
                        onDayClick
                            ? (state: { readonly activeLabel?: unknown }) => {
                                  // Recharts hands back the active category
                                  // label; map it to the row's raw ET date key.
                                  const point = data.find(
                                      (row) => row.label === state.activeLabel,
                                  );
                                  if (point !== undefined) {
                                      onDayClick(point.day);
                                  }
                              }
                            : undefined
                    }
                    className={onDayClick ? 'cursor-pointer' : undefined}
                >
                    <CartesianGrid
                        stroke="var(--border)"
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip content={<TrendTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {SERIES.map((series) => (
                        <Bar
                            key={series.key}
                            dataKey={series.key}
                            name={series.label}
                            stackId="outcomes"
                            fill={series.color}
                            isAnimationActive={false}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
